import { useEffect, useState, createContext } from 'react';

import { useProductThemeNew } from 'hooks/useProductTheme';
import useSessionStorage from 'hooks/useSessionStorage';

import { ProductFlowProps } from 'utils/productFlow';
import type { CartProviderProps } from './CartContextTypes';
import type { AppContextProps, ProductThemeProps } from 'hooks/useCart';

import { DATA_FORM } from '../constants';
import { useRouterQuery } from 'hooks/useRouterQuery';
import { Business } from 'types';
import type { OfferPlanAvailability } from 'services/repositories/products/getProductDataByPlanId/getProductDataByPlanId';

const CartContext = createContext({} as AppContextProps);

export function CartProvider({ children, pageProps }: CartProviderProps) {
  const routerQuery = useRouterQuery();
  const [dsBrand, setDsBrand] = useState(pageProps.dsBrand);
  const [dsMode, setDsMode] = useState(pageProps.dsMode);

  const [pagePropsBusiness] = useState(pageProps.business);

  const [pagePropsProductSlug] = useState(
    (routerQuery['product-slug'] as string) || pageProps.productCode
  );

  const [pagePropsProduct] = useState(pageProps.product);

  const themeNew = useProductThemeNew({
    product: pagePropsProduct,
    dsMode,
    dsBrand,
    area: pageProps.area,
    productSlug: pagePropsProductSlug,
    business: pagePropsBusiness as Business,
  });

  const [legacyProductTheme] = useState(themeNew);

  const _ga = pageProps._ga;
  const product = pageProps.product;

  const rawQuery = pageProps.rawQuery;
  const environment = pageProps.environment;
  const area = pageProps.area;
  const allCheckoutFlowsByProductCode = pageProps.allCheckoutFlowsByProductCode;
  const productCode = pageProps.productCode || themeNew?.data?.productCode;

  const isLiveEnabled = pageProps.isLiveEnabled || false;

  const [productBusiness] = useState<Business>(
    (pageProps.business || themeNew?.data?.business) as Business
  );

  const [mainProductTheme, setMainProductTheme] = useState<{
    color: ProductThemeProps['color'];
  } | null>(null);

  const [userName, setUserName] = useState<string | null>(null);
  const [productFlow, setProductFlow] = useState<ProductFlowProps | null>(
    pageProps.productFlow
  );
  // FIN-8016: useState (e não pageProps direto) para sobreviver à navegação no
  // cliente, onde o _app não busca o plano de novo.
  // FIN-8011: o setter recebe o valor conferido no BFF no envio do lead.
  const [offerAvailability, setOfferAvailability] =
    useState<OfferPlanAvailability | null>(pageProps.offerAvailability ?? null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userLeadId, setUserLeadId] = useState<string | null>(null);
  const [purchaseToken, setPurchaseToken] = useState<string | null>(null);

  const [productTheme, setProductTheme] = useState<{
    color: ProductThemeProps['color'];
  } | null>(themeNew);

  const [redirectSuccess, setRedirectSuccess] = useState('/login');
  const [sellerName, setSellerName] = useState(pageProps.sellerName || null);
  const [sellerAccount] = useState(pageProps.sellerAccount || null);
  const [hasAddressFormOnPayment] = useState(pageProps.hasAddressForm || false);
  const [loading, setLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [dataForm] = useSessionStorage(DATA_FORM, null);
  const [shouldUseUpsellTheme] = useSessionStorage('shouldUseUpsellTheme');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setUserEmail(dataForm?.email);
    setUserName(dataForm?.name);
    setUserPhone(dataForm?.phone);
  }, [dataForm]);

  // @TODO: Verificar como podemos remover a dependencia do theme
  useEffect(() => {
    if (!shouldUseUpsellTheme) {
      setProductTheme(themeNew);
    }
    setMainProductTheme(themeNew);
  }, [themeNew, shouldUseUpsellTheme]);

  return (
    isMounted && (
      <CartContext.Provider
        value={{
          mainProductTheme,
          legacyProductTheme,
          userName,
          userPhone,
          userEmail,
          userLeadId,
          purchaseToken,
          _ga,
          productTheme,
          redirectSuccess,
          product,
          isLiveEnabled,
          sellerName,
          rawQuery,
          environment,
          area,
          loading,
          productFlow,
          productBusiness,
          productCode,
          sellerAccount,
          dsMode,
          dsBrand,
          allCheckoutFlowsByProductCode,
          hasAddressFormOnPayment,
          offerAvailability,
          setOfferAvailability,
          setDsBrand,
          setDsMode,
          setLoading,
          setSellerName,
          setRedirectSuccess,
          setProductTheme,
          setUserName,
          setUserPhone,
          setUserEmail,
          setUserLeadId,
          setPurchaseToken,
          setProductFlow,
        }}
      >
        {children}
      </CartContext.Provider>
    )
  );
}

export default CartContext;
